import {
    useEffect,
    useMemo,
    useRef,
    useState,
    type ChangeEvent,
    type CSSProperties,
} from 'react';
import {
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Switch,
    useTheme,
} from '@mui/material';
import {
    AddPhotoAlternateOutlined,
    Close as CloseIcon,
    InsertDriveFileOutlined,
    NotesOutlined,
} from '@mui/icons-material';
import Autocomplete from '@/components/ui/Autocomplete/Autocomplete';
import Box from '@/components/ui/Box/Box';
import Button from '@/components/ui/Button/Button';
import CurrencyInput from '@/components/ui/CurrencyInput/CurrencyInput';
import FieldWrapper from '@/components/ui/FieldWrapper/FieldWrapper';
import IconButton from '@/components/ui/IconButton/IconButton';
import TextField from '@/components/ui/TextField/TextField';
import Typography from '@/components/ui/Typography/Typography';
import ConfirmDialog from '@/components/business/ConfirmDialog/ConfirmDialog';
import { useCompany } from '@/providers/CompanyProvider/CompanyProvider';
import { useNewJob } from '@/providers/NewJobProvider/NewJobProvider';
import { useRepairers } from '@/providers/RepairersProvider/RepairersProvider';
import { catalogApi, type CatalogEntry } from '@/api/catalog';
import { estimateApi, type EstimateEntry, type EstimatePrice } from '@/api/estimate';
import { buildPartLabel } from '@/helpers/catalogParts';
import type { NewServiceModalProps } from './INewServiceModal';
import type {
    NewJobServiceState,
    ServiceMedia,
} from '@/providers/NewJobProvider/NewJobProvider';
import type { Repairer } from '@/types/types';
import { styles } from './styles';

const KEYS_SERVICE_TYPE = 'Keys';

const ACCEPTED_MIME = 'image/jpeg,image/png,image/webp,image/heic,video/mp4,video/quicktime,application/pdf';
const ACCEPTED_PREFIXES = ['image/', 'video/'];
const ACCEPTED_EXACT = new Set(['application/pdf']);

const isAcceptedFile = (file: File): boolean =>
    ACCEPTED_PREFIXES.some((p) => file.type.startsWith(p)) ||
    ACCEPTED_EXACT.has(file.type);

const formatMoney = (n: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

const mediaKindFromFile = (file: File): ServiceMedia['media_type'] => {
    if (file.type.startsWith('image/')) return 'Image';
    if (file.type.startsWith('video/')) return 'Video';
    if (file.type === 'application/pdf') return 'PDF';
    return 'Text';
};

const readAsDataUrl = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });

const NewServiceModal = ({ open, editingId, onClose }: NewServiceModalProps) => {
    const theme = useTheme();
    const newJob = useNewJob();
    const companyCtx = useCompany();
    const repairersCtx = useRepairers();
    const isRepairer = companyCtx.state.company?.marketplace_side === 'Repairer';

    const [type, setType] = useState('');
    const [repair, setRepair] = useState('');
    const [parts, setParts] = useState<string[]>([]);
    const [price, setPrice] = useState('0');
    const [prices, setPrices] = useState<string[]>(['0']);
    const [priceEach, setPriceEach] = useState(false);
    const [repairer, setRepairer] = useState<Repairer | null>(null);
    const [media, setMedia] = useState<ServiceMedia[]>([]);
    const [noteDraft, setNoteDraft] = useState('');
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);
    const [catalog, setCatalog] = useState<CatalogEntry[]>([]);
    const [catalogLoading, setCatalogLoading] = useState(false);
    const [estimateEntries, setEstimateEntries] = useState<EstimateEntry[]>([]);
    const [estimateLoading, setEstimateLoading] = useState(false);
    const [estimatePrices, setEstimatePrices] = useState<EstimatePrice[]>([]);
    const [lockedParts, setLockedParts] = useState(false);
    const [lockedPrice, setLockedPrice] = useState(false);

    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const isEditing = editingId != null && editingId > 0;
    const vin = newJob.state.vin;
    const locationId = newJob.state.location?.id;

    useEffect(() => {
        if (!open || catalog.length > 0) return;
        let cancelled = false;
        setCatalogLoading(true);
        void (async () => {
            try {
                const res = await catalogApi.list();
                if (!cancelled) setCatalog(res.data?.payload ?? []);
            } catch {
            } finally {
                if (!cancelled) setCatalogLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [open, catalog.length]);

    const serviceTypeOptions = useMemo(
        () => Array.from(new Set(catalog.map((c) => c.service_type).filter(Boolean))) as string[],
        [catalog],
    );

    const fields = useMemo(() => {
        const byType = catalog.filter((c) => c.service_type === type);
        const repairTypes = Array.from(
            new Set(byType.map((c) => c.repair_type).filter(Boolean)),
        ) as string[];
        const scoped = repair ? byType.filter((c) => c.repair_type === repair) : byType;
        const parts = Array.from(
            new Set(scoped.map((c) => buildPartLabel(c.part, c.position)).filter(Boolean)),
        );
        return { repairTypes, parts };
    }, [catalog, type, repair]);

    const showRepairType = fields.repairTypes.length > 0;
    const showPart = fields.parts.length > 0;

    useEffect(() => {
        if (!open || type !== KEYS_SERVICE_TYPE || !vin || !locationId) {
            setEstimateEntries([]);
            setEstimateLoading(false);
            return;
        }
        let cancelled = false;
        setEstimateLoading(true);
        void (async () => {
            try {
                const res = await estimateApi.get({
                    vehicle: { vin },
                    location_id: locationId,
                    services: [{ service_details: { service_type: KEYS_SERVICE_TYPE } }],
                });
                if (!cancelled) setEstimateEntries(res.data?.payload?.services?.[0]?.estimates ?? []);
            } catch {
                if (!cancelled) setEstimateEntries([]);
            } finally {
                if (!cancelled) setEstimateLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [open, type, vin, locationId]);

    useEffect(() => {
        if (type !== KEYS_SERVICE_TYPE || !repair || estimateEntries.length === 0) {
            setLockedParts(false);
            setEstimatePrices([]);
            return;
        }
        const match = estimateEntries.find((e) => e.service_details.repair_type === repair);
        if (!match) {
            setLockedParts(false);
            setEstimatePrices([]);
            return;
        }
        const label = buildPartLabel(match.service_details.part, match.service_details.position);
        setParts(label ? [label] : []);
        setLockedParts(true);
        setEstimatePrices(match.prices ?? []);
    }, [type, repair, estimateEntries]);

    useEffect(() => {
        if (!repairer?.id || estimatePrices.length === 0) {
            setLockedPrice(false);
            return;
        }
        const found = estimatePrices.find(
            (p) => p.id === repairer.id || (p.name != null && p.name === repairer.name),
        );
        if (found?.price != null) {
            const value = String(found.price);
            setPrice(value.includes('.') ? value : `${value}.00`);
            setLockedPrice(true);
        } else {
            setPrice('0');
            setLockedPrice(false);
        }
    }, [repairer, estimatePrices]);

    useEffect(() => {
        if (!open) return;
        if (isEditing) {
            const existing = newJob.state.services.find((s) => s.id === editingId);
            if (existing) {
                setType(existing.type);
                setRepair(existing.repair);
                setParts(existing.parts ?? []);
                setPrice(existing.price);
                const qtyForPrices = Math.max(existing.parts?.length ?? 0, 1);
                setPrices(
                    existing.prices?.length
                        ? existing.prices
                        : Array.from({ length: qtyForPrices }, () => existing.price),
                );
                setPriceEach(existing.priceEach);
                setRepairer(
                    existing.repairer.id
                        ? { id: existing.repairer.id, name: existing.repairer.name ?? '' }
                        : null,
                );
                setMedia(existing.media ?? []);
            }
        } else {
            setType('');
            setRepair('');
            setParts([]);
            setPrice('0');
            setPrices(['0']);
            setPriceEach(false);
            setRepairer(null);
            setMedia([]);
        }
        setNoteDraft('');
        setUploadError(null);
    }, [open, editingId, isEditing]);

    useEffect(() => {
        if (!open || isRepairer) return;
        void repairersCtx.actions.refreshRepairers();
    }, [open, isRepairer]);

    const qty = Math.max(parts.length, 1);

    useEffect(() => {
        if (!priceEach) return;
        setPrices((prev) => {
            if (prev.length === qty) return prev;
            if (prev.length < qty) {
                return [...prev, ...Array.from({ length: qty - prev.length }, () => '0')];
            }
            return prev.slice(0, qty);
        });
    }, [qty, priceEach]);

    const total = priceEach
        ? prices.reduce((sum, p) => sum + (parseFloat(p) || 0), 0)
        : (parseFloat(price) || 0) * qty;

    const baseline = useMemo(() => {
        if (isEditing) {
            const existing = newJob.state.services.find((s) => s.id === editingId);
            return existing
                ? JSON.stringify({
                    type: existing.type,
                    repair: existing.repair,
                    parts: existing.parts ?? [],
                    price: existing.price,
                    prices: existing.prices,
                    priceEach: existing.priceEach,
                    repairer: existing.repairer,
                    media: existing.media,
                })
                : '';
        }
        return '';
    }, [open, isEditing, editingId]);

    const currentSnapshot = JSON.stringify({
        type,
        repair,
        parts,
        price,
        prices,
        priceEach,
        repairer: repairer ? { id: repairer.id, name: repairer.name } : {},
        media,
    });

    const isDirty = currentSnapshot !== baseline;

    const repairRequired = fields.repairTypes.length > 0;
    const partsRequired = fields.parts.length > 0;
    const canSubmit =
        Boolean(type) &&
        (!repairRequired || Boolean(repair)) &&
        (!partsRequired || parts.length > 0) &&
        (isRepairer || Boolean(repairer?.id));

    const handleFiles = async (e: ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;
        const accepted: File[] = [];
        const rejected: string[] = [];
        for (const file of Array.from(files)) {
            if (isAcceptedFile(file)) {
                accepted.push(file);
            } else {
                rejected.push(file.name);
            }
        }
        if (rejected.length) {
            setUploadError(
                `Unsupported file${rejected.length === 1 ? '' : 's'}: ${rejected.join(', ')}. ` +
                    'Only images, videos, and PDFs are allowed.',
            );
        } else {
            setUploadError(null);
        }
        const added: ServiceMedia[] = [];
        for (const file of accepted) {
            const dataUrl = await readAsDataUrl(file);
            added.push({
                id: `${Date.now()}-${file.name}`,
                name: file.name,
                media_type: mediaKindFromFile(file),
                dataUrl,
            });
        }
        setMedia((prev) => [...prev, ...added]);
        e.target.value = '';
    };

    const addNoteAsMedia = () => {
        const text = noteDraft.trim();
        if (!text) return;
        setMedia((prev) => [
            ...prev,
            { id: `note-${Date.now()}`, name: 'Note', media_type: 'Text', text },
        ]);
        setNoteDraft('');
    };

    const removeMedia = (id: string) =>
        setMedia((prev) => prev.filter((m) => m.id !== id));

    const handleAttemptClose = () => {
        if (isDirty) {
            setConfirmCloseOpen(true);
            return;
        }
        onClose();
    };

    const handleSubmit = () => {
        if (!canSubmit) return;
        const payload: NewJobServiceState = {
            id: isEditing
                ? (editingId as number)
                : (newJob.state.services.at(-1)?.id ?? 0) + 1,
            type,
            repair,
            parts,
            position: '',
            price,
            prices: priceEach ? prices.slice(0, qty) : Array.from({ length: qty }, () => price),
            priceEach,
            currency: 'USD',
            totalDP: formatMoney(total),
            repairer: repairer ? { id: repairer.id, name: repairer.name } : {},
            media,
        };
        newJob.DISPATCH({
            type: isEditing ? 'EDIT_SERVICE' : 'NEW_SERVICE',
            payload,
        });
        onClose();
    };

    const partLabel = (i: number) => {
        const name = parts[i];
        const suffix = name ? ` — ${name}` : '';
        return `Price ${i + 1}${suffix}`;
    };

    const optionsLoadingText = (
        <Box sx={styles.optionsLoadingRow}>
            <CircularProgress size={16} />
            <Typography sx={styles.partsLoadingText}>Loading options…</Typography>
        </Box>
    );

    return (
        <>
            <Dialog
                open={open}
                onClose={handleAttemptClose}
                maxWidth="md"
                fullWidth
                sx={styles.dialog}
            >
                <DialogTitle sx={styles.title}>
                    {isEditing ? 'Edit Service' : 'Add Service'}
                </DialogTitle>
                <DialogContent sx={styles.content}>
                    <FieldWrapper filled={Boolean(type)} required>
                        <Autocomplete<string>
                            options={serviceTypeOptions}
                            value={type || null}
                            onChange={(_, value) => {
                                setType(value ?? '');
                                setRepair('');
                                setParts([]);
                            }}
                            label="Service Type"
                            required
                            loading={catalogLoading}
                            loadingText={optionsLoadingText}
                        />
                    </FieldWrapper>

                    {showRepairType && (
                        <FieldWrapper filled={Boolean(repair)} required={repairRequired}>
                            <Autocomplete<string, false, false, true>
                                options={fields.repairTypes}
                                value={repair || null}
                                onChange={(_, value) => setRepair((value as string) ?? '')}
                                label="Repair Type"
                                required={repairRequired}
                                freeSolo
                                loading={catalogLoading}
                                loadingText={optionsLoadingText}
                            />
                        </FieldWrapper>
                    )}

                    {estimateLoading && (
                        <Box sx={styles.partsLoadingRow}>
                            <CircularProgress size={16} />
                            <Typography sx={styles.partsLoadingText}>
                                Checking for preselected parts…
                            </Typography>
                        </Box>
                    )}

                    {showPart && (
                        <FieldWrapper filled={parts.length > 0} required>
                            <Autocomplete<string, true, false, true>
                                multiple
                                disableCloseOnSelect
                                disabled={lockedParts}
                                limitTags={3}
                                options={fields.parts}
                                value={parts}
                                onChange={(_, value) => setParts(value as string[])}
                                label="Part(s)"
                                required
                                freeSolo
                                loading={catalogLoading}
                                loadingText={optionsLoadingText}
                                sx={lockedParts ? styles.lockedPartsField(theme) : undefined}
                            />
                        </FieldWrapper>
                    )}

                    {!isRepairer && (
                        <FieldWrapper filled={Boolean(repairer?.id)} required>
                            <Autocomplete<Repairer>
                                options={repairersCtx.state.repairers}
                                getOptionLabel={(o) => o.name ?? ''}
                                value={repairer}
                                onChange={(_, value) => setRepairer(value)}
                                label="Repairer"
                                required
                            />
                        </FieldWrapper>
                    )}

                    <Box sx={styles.priceSection(theme)}>
                        <Box sx={styles.priceHeaderRow}>
                            <Typography sx={styles.priceTitle}>Set Price</Typography>
                            {qty > 1 && (
                                <Box sx={styles.priceToggleRow}>
                                    <Typography sx={styles.priceToggleLabel}>
                                        Price individually
                                    </Typography>
                                    <Switch
                                        size="small"
                                        checked={priceEach}
                                        onChange={(e) => setPriceEach(e.target.checked)}
                                    />
                                </Box>
                            )}
                        </Box>
                        {priceEach && qty > 1 ? (
                            <Box sx={styles.pricesList}>
                                {prices.map((p, i) => (
                                    <CurrencyInput
                                        key={i}
                                        fullWidth
                                        size="small"
                                        label={partLabel(i)}
                                        value={p}
                                        onChange={(next) =>
                                            setPrices((prev) => {
                                                const arr = [...prev];
                                                arr[i] = next;
                                                return arr;
                                            })
                                        }
                                    />
                                ))}
                            </Box>
                        ) : (
                            <CurrencyInput
                                fullWidth
                                size="small"
                                label={qty > 1 ? `Price × ${qty}` : 'Price'}
                                value={price}
                                onChange={setPrice}
                                disabled={lockedPrice}
                            />
                        )}
                        <Divider sx={{ my: 0.5 }} />
                        <Box sx={styles.totalRow}>
                            <Typography sx={styles.totalLabel}>Total</Typography>
                            <Typography sx={styles.totalValue}>{formatMoney(total)}</Typography>
                        </Box>
                    </Box>
                    <Typography sx={styles.sectionLabel}>Media & Notes</Typography>
                    <Box sx={styles.mediaInputRow}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Add a quick note…"
                            value={noteDraft}
                            onChange={(e) => setNoteDraft(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    addNoteAsMedia();
                                }
                            }}
                            sx={{ flex: 1, minWidth: 0 }}
                        />
                        <Button
                            variant="outlined"
                            size="small"
                            sx={styles.uploadBtn(theme)}
                            onClick={addNoteAsMedia}
                            disabled={!noteDraft.trim()}
                            startIcon={<NotesOutlined />}
                        >
                            Add note
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            sx={styles.uploadBtn(theme)}
                            onClick={() => fileInputRef.current?.click()}
                            startIcon={<AddPhotoAlternateOutlined />}
                        >
                            Upload
                        </Button>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept={ACCEPTED_MIME}
                            multiple
                            style={styles.hiddenInput as CSSProperties}
                            onChange={(e) => {
                                void handleFiles(e);
                            }}
                        />
                    </Box>
                    {uploadError && (
                        <Typography sx={styles.uploadError}>{uploadError}</Typography>
                    )}
                    {media.length > 0 && (
                        <Box sx={styles.mediaRow}>
                            {media.map((m) => (
                                <Box key={m.id} sx={styles.mediaChip(theme)}>
                                    {m.media_type === 'Image' && m.dataUrl ? (
                                        <img
                                            src={m.dataUrl}
                                            alt={m.name}
                                            style={styles.mediaThumb as CSSProperties}
                                        />
                                    ) : m.media_type === 'Text' ? (
                                        <>
                                            <NotesOutlined fontSize="small" />
                                            <Typography sx={{ fontSize: '12px' }}>
                                                {m.text}
                                            </Typography>
                                        </>
                                    ) : (
                                        <>
                                            <InsertDriveFileOutlined fontSize="small" />
                                            <Typography sx={{ fontSize: '12px' }}>
                                                {m.name}
                                            </Typography>
                                        </>
                                    )}
                                    <IconButton size="small" onClick={() => removeMedia(m.id)}>
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            ))}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={styles.actions}>
                    <Button
                        variant="contained"
                        disabled={!canSubmit}
                        onClick={handleSubmit}
                        sx={styles.doneBtn(theme)}
                    >
                        Done
                    </Button>
                </DialogActions>
            </Dialog>
            <ConfirmDialog
                open={confirmCloseOpen}
                title="Discard service changes?"
                message="You have unsaved changes. Closing now will discard them."
                confirmLabel="Discard"
                cancelLabel="Stay"
                danger
                onConfirm={() => {
                    setConfirmCloseOpen(false);
                    onClose();
                }}
                onCancel={() => setConfirmCloseOpen(false)}
            />
        </>
    );
};

export default NewServiceModal;
